import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { isLayeredGraphicScene } from 'js/src/agent/html-design/layeredGraphic';
import {
    currentDesign,
    describeVideo,
    SCENE_HTML_RENDERING_RULES,
} from 'js/src/agent/html-design/sceneDesignFacts';
import { formatSceneNarration } from 'js/src/agent/html-design/sceneNarration';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { sceneDisplayId } from 'js/src/agent/tools/scene-addressing';
import type { RootState } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

import { describeBox } from './graphicChecks';
import type { FrameBox } from './speakerFaces';

/** Where the speaker is, from face detection when it ran, or from the still. */
function describeSpeaker(heads: readonly FrameBox[] | null): string {
    if (heads === null) {
        return "- The speaker, if there is one: find them in the still you're given.";
    }
    if (heads.length === 0) {
        return '- The speaker: no face was found in the recording at this point; check the still.';
    }
    return `- The speaker's head${heads.length > 1 ? 's' : ''}, in frame pixels: ${heads.map(describeBox).join('; ')}. Nothing you draw may touch ${heads.length > 1 ? 'them' : 'it'}.`;
}

function describeCaptions(scene: Scene): string {
    const captions = scene.sceneGroup?.captions;
    if (!captions?.visible || !captions.asset) {
        return '- Captions: off.';
    }
    const { x, y, width, height } = captions.asset.approximateMaxBounds;
    return `- Captions: on, in the band ${describeBox({
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(width),
        height: Math.round(height),
    })}. Keep it clear.`;
}

/** The latest other layered graphic in the video, so a video's graphics read as one set. */
function siblingGraphic(luminary: Luminary, scene: Scene): string {
    const sibling = luminary.scenes
        .filter((other) => other.id !== scene.id && isLayeredGraphicScene(other))
        .map((other) => ({
            other,
            html: (other.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as HtmlAsset | undefined)
                ?.html,
        }))
        .filter(({ html }) => Boolean(html))
        .at(-1);
    return sibling
        ? `\n## Another graphic in this video\nMatch its type, colours, plates and motion so the video's graphics read as one set; don't copy its content.\n### scene_id ${sceneDisplayId(luminary, sibling.other.id)}\n${sibling.html}\n`
        : '';
}

/**
 * The whole system prompt of a graphic subagent: what it is for, how layering over a speaker works,
 * the renderer facts, and the scene's facts — the frame, where the speaker and captions are, the
 * timed words over it, the palette and fonts — with the design to revise when there is one. The
 * still of the scene beneath comes in the first user message, with the brief.
 */
export function getGraphicScenePrompt(
    luminary: Luminary,
    state: RootState,
    sceneId: string,
    heads: readonly FrameBox[] | null,
): string {
    const scene = luminary.scenes.find((candidate) => candidate.id === sceneId);
    if (!scene) {
        return '';
    }
    const narration = formatSceneNarration(scene, state);
    const existing = currentDesign(luminary, [sceneId]);
    return `You design one animated graphic layered over a scene of a Lumen5 video. The scene already shows something, usually a person speaking to camera, sometimes footage or a stock design, and it stays on screen beneath your graphic for the whole scene. Your graphic shows what the speaker can't by talking: the diagram, the comparison, the steps, the figures and how they relate.

You're used where the video's stock designs (a lower third, a callout card, a short list) don't fit, so the brief is usually specific: a diagram, a list longer than a card holds or one that builds point by point, figures that relate, or a placement the user asked for. Do what it asks.

## How a scene's HTML is rendered
${SCENE_HTML_RENDERING_RULES}
- The page is transparent: give \`html\`, \`body\` and any full-frame container no background. Whatever you leave unpainted shows the scene beneath.

## Layering over the speaker
- Keep off the speaker. Put the graphic in the clear space beside or above them, on whichever side has more room; over footage or a design, keep off its subject and its own text.
- Paint at most a band or a side of the frame. More than 40% of it painted at once hides the scene and is refused.
- Text over the scene beneath needs contrast you can't measure: set it on a plate (a solid or frosted panel) unless the area behind it is plain and your colour clearly reads against it.
- It should feel like part of the shot, lighter than a card: viewers who wanted a clean video should still welcome it.

## Make it work as part of a video
- Show the idea, not the sentence: the figure, its comparison, the steps, the diagram, each with at most a short label. Never put the narration on screen as sentences.
- Bring each element in on the word that states it (the narration below gives each word's time), build up as the speaker goes, and hold the whole once it's built. It may clear in the scene's last half second.
- Use only figures, names and words from the brief or the narration; never invent one.
- Restrained type: one weight for the key figure or word, a lighter one for labels, one accent colour from the video's palette.

## The scene
${describeVideo(luminary, [sceneId])}
${describeSpeaker(heads)}
${describeCaptions(scene)}
${narration ? `\n${narration}\n` : ''}${siblingGraphic(luminary, scene)}${existing ? `\n## The scene's current graphic\nThe brief asks for changes to this. Keep what it doesn't mention.\n${existing}\n` : ''}
## What to do
Look at the still of the scene beneath, then call \`${TOOL_NAMES.writeSceneHtml}\` with the complete document. It checks that the graphic runs, draws something, leaves the scene beneath visible and keeps off the speaker; if it doesn't, you'll be told why and can write it once more.
`;
}
